import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
import {
  FiAlertTriangle,
  FiVolumeX,
  FiSlash,
  FiX,
  FiAlertCircle,
} from "react-icons/fi";
import communityService from "../../../services/communityService";
import "./collab-moderation.css";

// Animation variants
const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.2 } },
  exit: { opacity: 0, transition: { duration: 0.15 } },
};

const modalVariants = {
  hidden: { opacity: 0, scale: 0.95, y: 20 },
  visible: { opacity: 1, scale: 1, y: 0, transition: { duration: 0.25 } },
  exit: { opacity: 0, scale: 0.95, y: 20, transition: { duration: 0.15 } },
};

// =================== WARN MODAL ===================
export function WarnModal({
  isOpen,
  memberId,
  memberName,
  communityId,
  onClose,
  onSuccess,
}) {
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  const handleWarn = async () => {
    if (!reason.trim()) {
      toast.error("Please provide a reason for the warning");
      return;
    }

    setLoading(true);
    try {
      const response = await communityService.warnUser(
        communityId,
        memberId,
        reason
      );
      toast.success(response.message || "Warning issued successfully");

      // Check if user was escalated (auto-banned after 3 warnings)
      if (response.data?.escalated) {
        toast.warning(
          `${memberName} has been automatically banned due to multiple warnings`
        );
      }

      setReason("");
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(error.message || "Failed to warn user");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setReason("");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="mod-modal-overlay"
        variants={overlayVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        onClick={handleClose}
      >
        <motion.div
          className="mod-modal-container mod-modal-warn"
          variants={modalVariants}
          onClick={(e) => e.stopPropagation()}
        >
          <button className="mod-modal-close" onClick={handleClose}>
            <FiX />
          </button>

          <div className="mod-modal-header">
            <div className="mod-modal-icon warn">
              <FiAlertTriangle />
            </div>
            <h2>Warn {memberName}</h2>
          </div>

          <div className="mod-modal-info">
            <FiAlertCircle />
            <span>After 3 warnings, the user will be automatically banned.</span>
          </div>

          <div className="mod-modal-body">
            <label htmlFor="warn-reason">Reason for warning</label>
            <textarea
              id="warn-reason"
              placeholder="Explain why you're issuing this warning..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              maxLength={500}
            />
            <div className="mod-char-count">{reason.length}/500</div>
          </div>

          <div className="mod-modal-actions">
            <button
              className="mod-btn mod-btn-cancel"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              className="mod-btn mod-btn-warn"
              onClick={handleWarn}
              disabled={loading || !reason.trim()}
            >
              {loading ? "Issuing Warning..." : "Issue Warning"}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// =================== MUTE MODAL ===================
export function MuteModal({
  isOpen,
  memberId,
  memberName,
  communityId,
  onClose,
  onSuccess,
}) {
  const [reason, setReason] = useState("");
  const [duration, setDuration] = useState(24);
  const [loading, setLoading] = useState(false);

  const durations = [
    { value: 1, label: "1 hour" },
    { value: 6, label: "6 hours" },
    { value: 12, label: "12 hours" },
    { value: 24, label: "24 hours" },
    { value: 48, label: "2 days" },
    { value: 168, label: "1 week" },
  ];

  const handleMute = async () => {
    if (!reason.trim()) {
      toast.error("Please provide a reason for muting");
      return;
    }

    setLoading(true);
    try {
      const response = await communityService.muteUser(
        communityId,
        memberId,
        reason,
        duration
      );
      toast.success(response.message || "User muted successfully");
      setReason("");
      setDuration(24);
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(error.message || "Failed to mute user");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setReason("");
    setDuration(24);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="mod-modal-overlay"
        variants={overlayVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        onClick={handleClose}
      >
        <motion.div
          className="mod-modal-container mod-modal-mute"
          variants={modalVariants}
          onClick={(e) => e.stopPropagation()}
        >
          <button className="mod-modal-close" onClick={handleClose}>
            <FiX />
          </button>

          <div className="mod-modal-header">
            <div className="mod-modal-icon mute">
              <FiVolumeX />
            </div>
            <h2>Mute {memberName}</h2>
          </div>

          <div className="mod-modal-info">
            <FiAlertCircle />
            <span>
              User will not be able to post messages or replies during mute
              period.
            </span>
          </div>

          <div className="mod-modal-body">
            <label htmlFor="mute-duration">Mute duration</label>
            <select
              id="mute-duration"
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
            >
              {durations.map((d) => (
                <option key={d.value} value={d.value}>
                  {d.label}
                </option>
              ))}
            </select>

            <label htmlFor="mute-reason">Reason for muting</label>
            <textarea
              id="mute-reason"
              placeholder="Explain why you're muting this user..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              maxLength={500}
            />
            <div className="mod-char-count">{reason.length}/500</div>
          </div>

          <div className="mod-modal-actions">
            <button
              className="mod-btn mod-btn-cancel"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              className="mod-btn mod-btn-mute"
              onClick={handleMute}
              disabled={loading || !reason.trim()}
            >
              {loading ? "Muting User..." : "Mute User"}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// =================== BAN MODAL ===================
export function BanModal({
  isOpen,
  memberId,
  memberName,
  communityId,
  onClose,
  onSuccess,
}) {
  const [reason, setReason] = useState("");
  const [duration, setDuration] = useState(168);
  const [permanent, setPermanent] = useState(false);
  const [loading, setLoading] = useState(false);

  const durations = [
    { value: 24, label: "1 day" },
    { value: 72, label: "3 days" },
    { value: 168, label: "1 week" },
    { value: 720, label: "1 month" },
  ];

  const handleBan = async () => {
    if (!reason.trim()) {
      toast.error("Please provide a reason for the ban");
      return;
    }

    setLoading(true);
    try {
      const response = await communityService.banUser(
        communityId,
        memberId,
        reason,
        permanent ? null : duration
      );
      toast.success(response.message || "User banned successfully");
      setReason("");
      setDuration(168);
      setPermanent(false);
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(error.message || "Failed to ban user");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setReason("");
    setDuration(168);
    setPermanent(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="mod-modal-overlay"
        variants={overlayVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        onClick={handleClose}
      >
        <motion.div
          className="mod-modal-container mod-modal-ban"
          variants={modalVariants}
          onClick={(e) => e.stopPropagation()}
        >
          <button className="mod-modal-close" onClick={handleClose}>
            <FiX />
          </button>

          <div className="mod-modal-header">
            <div className="mod-modal-icon ban">
              <FiSlash />
            </div>
            <h2>Ban {memberName}</h2>
          </div>

          <div className="mod-modal-info warning">
            <FiAlertCircle />
            <span>
              User will be removed from the community and cannot rejoin until
              ban expires.
            </span>
          </div>

          <div className="mod-modal-body">
            <label className="mod-checkbox-label">
              <input
                type="checkbox"
                checked={permanent}
                onChange={(e) => setPermanent(e.target.checked)}
              />
              <span>Permanent ban</span>
            </label>

            {!permanent && (
              <>
                <label htmlFor="ban-duration">Ban duration</label>
                <select
                  id="ban-duration"
                  value={duration}
                  onChange={(e) => setDuration(Number(e.target.value))}
                >
                  {durations.map((d) => (
                    <option key={d.value} value={d.value}>
                      {d.label}
                    </option>
                  ))}
                </select>
              </>
            )}

            <label htmlFor="ban-reason">Reason for ban</label>
            <textarea
              id="ban-reason"
              placeholder="Explain why you're banning this user..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              maxLength={500}
            />
            <div className="mod-char-count">{reason.length}/500</div>
          </div>

          <div className="mod-modal-actions">
            <button
              className="mod-btn mod-btn-cancel"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              className="mod-btn mod-btn-ban"
              onClick={handleBan}
              disabled={loading || !reason.trim()}
            >
              {loading
                ? "Banning..."
                : permanent
                ? "Ban Permanently"
                : "Ban User"}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default { WarnModal, MuteModal, BanModal };
